import { useMemo } from "react";

import { useUser } from "@components/user-context";
import { formatDate } from "@features/mmm/utils/formatters";
import { type FetchMmmSummary_me_mmm_summary_accuracyData_data } from "@north-beam/nb-client-side";
import { formatting } from "@north-beam/nb-common";
import { useSelectedCycle } from "@pages/mmm/hooks/use-selected-cycle";

import { type Kpi } from "../../explore-budgets/client-setting/types";

import { formatKpi } from "./utils";

interface Props {
  selectedKpi: Kpi;
  accuracyData: FetchMmmSummary_me_mmm_summary_accuracyData_data[];
}

export const SummaryAccuracySummaries = ({
  accuracyData,
  selectedKpi,
}: Props) => {
  const { user } = useUser();
  const { selectedCycle } = useSelectedCycle();

  const actualValue = useMemo(() => {
    return accuracyData.reduce(
      (acc, row) => acc + row.actualKpis[selectedKpi.kpi_name],
      0,
    );
  }, [accuracyData, selectedKpi]);

  const forecastValue = useMemo(() => {
    return accuracyData.reduce(
      (acc, row) => acc + row.predictedKpis[selectedKpi.kpi_name].value,
      0,
    );
  }, [accuracyData, selectedKpi.kpi_name]);

  const predictedRange = useMemo(() => {
    const forecastHigh = accuracyData.reduce(
      (acc, row) => acc + row.predictedKpis[selectedKpi.kpi_name].high,
      0,
    );
    const forecastLow = accuracyData.reduce(
      (acc, row) => acc + row.predictedKpis[selectedKpi.kpi_name].low,
      0,
    );

    return { from: forecastLow, to: forecastHigh };
  }, [accuracyData, selectedKpi.kpi_name]);

  const accuracyValue = useMemo(() => {
    return 1 - Math.abs(forecastValue - actualValue) / actualValue;
  }, [actualValue, forecastValue]);

  return (
    <div className="grid grid-cols-2 gap-3">
      <div className="border rounded-lg py-2 px-4 flex flex-col space-y-2">
        <div className="text-lg font-bold">Total Forecast Model Accuracy</div>
        <div className="text-xs font-medium tracking-wider">
          {formatDate(selectedCycle.date_range.start_date)} -{" "}
          {formatDate(selectedCycle.date_range.end_date)}
        </div>
        <div className="text-2xl text-cosmic">
          {`${formatting.formatPercentage(accuracyValue, {
            precision: "approximate",
          })} accurate`}
        </div>
      </div>
      <div className="border rounded-lg py-2 px-3 flex flex-col space-y-2">
        <div className="text-lg font-bold text-ellipsis truncate">
          Actual vs predicted, {selectedKpi.friendly_name}
        </div>
        <div className="text-xs font-medium tracking-wider">
          {formatDate(selectedCycle.date_range.start_date)} -{" "}
          {formatDate(selectedCycle.date_range.end_date)}
        </div>
        <div className="text-2xl flex space-x-2 truncate">
          <span className="text-cosmic">
            {formatKpi(selectedKpi.kpi_type, actualValue, user)}
          </span>
          <span className="text-gray+1">compared to</span>
          <span className="text-cosmic">
            {`${formatKpi(
              selectedKpi.kpi_type,
              predictedRange.from,
              user,
            )} - ${formatKpi(selectedKpi.kpi_type, predictedRange.to, user)}`}
          </span>
          <span className="text-gray+1">predicted</span>
        </div>
      </div>
    </div>
  );
};
